/**
 * Shared Adapter Utilities
 *
 * This file contains common constants, types, and utility functions used by both
 * Django and Fulcrum adapters. Following DRY principles to avoid code duplication.
 */

import { FieldMapping, RelatedEntityItem, ApiEndpoint, FlexibleDataItem } from "../../types";
import { constructThumbnailUrl } from "../temporaryUrlUtils";

// ============================================================================
// SHARED CONSTANTS
// ============================================================================

/**
 * Default field mapping for Fulcrum data sources
 * Used to extract standardized fields from Fulcrum form data
 */
export const DEFAULT_FULCRUM_MAPPING: FieldMapping = {
  id: ["id", "fulcrum_id"],
  name: ["ea1a", "Site Name", "site_name", "name", "label", "_title", "title", "weed_species_other", "species", "subject"],
  description: ["7153", "description", "notes", "comment", "comments"],
  created_at: ["created_at", "client_created_at", "date_recorded"],
  geometry: ["geometry"],

  transformers: {
    created_at: (value) => (value ? new Date(value).toISOString() : null),
    geometry: (value) => {
      if (typeof value === "object") return value;
      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      }
      return null;
    }
  }
};

/**
 * Shared Fulcrum field configuration for consistent field mapping across adapters
 * Maps Fulcrum field IDs to human-readable names with priority levels
 */
export const FULCRUM_FIELD_CONFIG: Record<string, { name: string; priority: "high" | "low" | "hidden" }> = {
  // Locations form field mappings (form: 899a7258-3e5f-4971-aaae-552aa6796520)
  d4bd: { name: "job id", priority: "high" },
  "89e3": { name: "name", priority: "high" },
  "5ddf": { name: "date recorded", priority: "high" },
  fc14: { name: "time", priority: "high" },
  "68af": { name: "description", priority: "high" },
  "4c2c": { name: "location type", priority: "low" },
  b143: { name: "photos", priority: "low" },
  "0002": { name: "photo caption", priority: "low" },
  "0001": { name: "record id", priority: "low" },
  "4071": { name: "form id", priority: "low" },
  // Weeds form field mappings
  "1c63": { name: "species", priority: "high" },
  "7650": { name: "title", priority: "high" },
  // Native Animals form
  fc2e: { name: "animal name", priority: "high" },
  // Cultural Sites form field mappings (form: 97898239-74f7-4495-92ad-32fb67a8f469)
  ea1a: { name: "Site Name", priority: "high" },
  "7153": { name: "description", priority: "high" },
  fbc6: { name: "site id", priority: "low" },
  // Jobs/Trips form field mappings (actual field names from data)
  job_id: { name: "job id", priority: "high" },
  job_description: { name: "job description", priority: "high" },
  scheduled_date: { name: "scheduled date", priority: "high" },
  scheduled_end_date: { name: "scheduled end date", priority: "high" },
  rangers: { name: "rangers", priority: "high" },
  date_entered: { name: "date entered", priority: "high" },
  time_of_year: { name: "time of year", priority: "high" },
  // Common fields across forms
  "631e": { name: "notes", priority: "high" },
  b85f: { name: "photos", priority: "low" },
  a4e2: { name: "videos", priority: "low" }
  // Add more field mappings as needed
};

/**
 * Common name field candidates for fallback name extraction
 */
export const COMMON_NAME_CANDIDATES = [
  "name",
  "title",
  "label",
  "site_name",
  "location_name",
  "entity_name",
  "item_name",
  "object_name",
  "display_name",
  "species",
  "animal_name",
  "job_description",
  "job_id",
  // Fulcrum schema-mapped field names (from form schemas)
  "Name of location",
  "Name of location ",
  "Site Name",
  "Location features"
];

/**
 * Common description field candidates for fallback description extraction
 */
export const COMMON_DESCRIPTION_CANDIDATES = [
  "description",
  "summary",
  "notes",
  "comment",
  "comments",
  "details",
  "info",
  "text",
  "content"
];

/**
 * Common Fulcrum field IDs for name extraction
 */
export const COMMON_FULCRUM_NAME_IDS = ["89e3", "7650", "fc2e", "1c63"];

/**
 * Common Fulcrum field IDs for description extraction
 */
export const COMMON_FULCRUM_DESC_IDS = ["68af", "631e", "f5ad"];

// ============================================================================
// SHARED UTILITY FUNCTIONS
// ============================================================================

/**
 * Extract nested value from an object using dot notation
 * @param obj - The object to extract from
 * @param path - The path to the value (e.g., 'properties.name')
 * @returns The extracted value or null if not found
 */
export const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return null;

  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Extract field value using multiple candidate field names
 * @param data - The data object to extract from
 * @param fieldPath - String or array of field paths to try
 * @param defaultValue - Default value if no field is found
 * @returns The extracted value or default value
 */
export const extractField = (data: any, fieldPath: string | string[] | undefined, defaultValue: any = null): any => {
  if (!fieldPath) return defaultValue;

  if (Array.isArray(fieldPath)) {
    for (const path of fieldPath) {
      const result = getNestedValue(data, path);
      if (result !== null && result !== undefined) {
        return result;
      }
    }
    return defaultValue;
  }

  return getNestedValue(data, fieldPath) ?? defaultValue;
};

/**
 * Check if a URL points to an image
 * @param url - The URL to check
 * @returns True if the URL appears to be an image
 */
export const isImageUrl = (url: string | null): boolean => {
  if (!url || typeof url !== "string") return false;

  // Check for common image extensions
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff)(\?|$)/i;
  if (imageExtensions.test(url)) return true;

  // Check for data URLs
  if (url.startsWith("data:image/")) return true;

  // Check for blob URLs (often used for uploaded images)
  if (url.startsWith("blob:")) return true;

  return false;
};

/**
 * Standardize ID to string format
 * @param id - The ID value (can be string, number, or undefined)
 * @returns Standardized string ID
 */
export const standardizeId = (id: any): string => {
  if (id === null || id === undefined) return "unknown";
  return String(id);
};

/**
 * Apply field transformer function if available
 * @param value - The value to transform
 * @param transformer - The transformer function
 * @returns The transformed value or original value if no transformer
 */
export const applyTransformer = (value: any, transformer?: (value: any) => any): any => {
  return transformer ? transformer(value) : value;
};

// ============================================================================
// FULCRUM-SPECIFIC UTILITIES
// ============================================================================

/**
 * Helper function to find form elements by type and optionally by label pattern
 * Used for dynamic form schema processing
 */
export const findElementByType = (elements: any[], types: string[], labelPattern?: string | null): any => {
  for (const element of elements) {
    if (types.includes(element.type)) {
      if (!labelPattern || element.label?.toLowerCase().includes(labelPattern.toLowerCase())) {
        return element;
      }
    }
    if (element.elements) {
      const found = findElementByType(element.elements, types, labelPattern);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Helper function to extract string value from various Fulcrum field types
 * Handles choice fields, other values, and complex field structures
 */
export const extractValueFromField = (value: any): string => {
  if (typeof value === "string") return value;
  if (typeof value === "object" && value) {
    // Handle choice fields
    if (value.choice_values && Array.isArray(value.choice_values)) {
      return value.choice_values.join(", ");
    }
    // Handle other values
    if (value.other_values && Array.isArray(value.other_values)) {
      return value.other_values.join(", ");
    }
    // Handle single value objects
    if (value.value) return value.value;
  }
  return String(value || "");
};

/**
 * Create form ID to relationship key mapping from API endpoints
 * This enables proper relationship traversal in the entity graph
 */
export const createFormToRelationshipMapping = (apiEndpoints: ApiEndpoint[]): Map<string, string> => {
  const mapping = new Map<string, string>();

  apiEndpoints.forEach((endpoint) => {
    if (endpoint.formId && endpoint.relationshipKey) {
      mapping.set(endpoint.formId, endpoint.relationshipKey);
    }
  });

  return mapping;
};

/**
 * Get field label from form schema
 * @param fieldId - The field ID to look up
 * @param formSchema - The form schema object
 * @returns The human-readable field label
 */
export const getFieldLabel = (fieldId: string, formSchema?: any): string => {
  if (!formSchema?.elements) {
    // Fallback to hardcoded config
    const fieldConfig = FULCRUM_FIELD_CONFIG[fieldId];
    return fieldConfig?.name || `Field ${fieldId}`;
  }

  // Search through form schema elements
  const findField = (elements: any[]): string | null => {
    for (const element of elements) {
      if (element.key === fieldId) {
        return element.label || element.data_name || fieldId;
      }
      if (element.elements) {
        const found = findField(element.elements);
        if (found) return found;
      }
    }
    return null;
  };

  return findField(formSchema.elements) || `Field ${fieldId}`;
};

// ============================================================================
// DATA SOURCE DETECTION
// ============================================================================

/**
 * Determine the data source type of an item
 * @param item - The data item to analyze
 * @param endpoint - Optional endpoint information
 * @returns The data source type ('fulcrum', 'django', or 'unknown')
 */
export const getDataSourceType = (item?: any, endpoint?: any): string => {
  // Check explicit data source type first
  if (item?.dataSourceType) {
    return item.dataSourceType;
  }

  if (endpoint?.dataSourceType) {
    return endpoint.dataSourceType;
  }

  // Check for Fulcrum-specific indicators
  if (item?.fulcrum_id || item?.form_id || item?.fulcrum_data || item?.form_values) {
    return "fulcrum";
  }

  // Check endpoint URL patterns
  if (endpoint?.api_endpoint?.includes("fulcrum")) {
    return "fulcrum";
  }

  // Default to Django for standard database items
  return "django";
};

/**
 * Extract first matching value from an object using case-insensitive field matching
 * Useful for finding values when field names might vary in case
 */
export const getFirstMatchingValueCI = (obj: any, fieldNames: string[]): any => {
  if (!obj || typeof obj !== "object") return null;

  const objKeys = Object.keys(obj);

  // Helper to normalize strings for comparison (lowercase, trimmed, underscores to spaces)
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[_\s]+/g, " ");

  for (const fieldName of fieldNames) {
    // 1. Try exact match first for performance
    if (obj[fieldName] !== undefined && obj[fieldName] !== null && obj[fieldName] !== "") {
      return obj[fieldName];
    }

    const normalizedFieldName = normalize(fieldName);

    // 2. Try normalized case-insensitive match (handles Activity type vs activity_type)
    const matchingKey = objKeys.find((key) => normalize(key) === normalizedFieldName);
    if (matchingKey && obj[matchingKey] !== undefined && obj[matchingKey] !== null && obj[matchingKey] !== "") {
      return obj[matchingKey];
    }

    // 3. Try "Other" variants common in Fulcrum (e.g., activity_type -> activity_type_other)
    const otherVariants = [normalizedFieldName + " other", normalizedFieldName + " (other)", normalizedFieldName + "_other"];

    const otherKey = objKeys.find((key) => {
      const normalizedKey = normalize(key);
      return otherVariants.includes(normalizedKey) || normalizedKey === normalizedFieldName + "other";
    });

    if (otherKey && obj[otherKey] !== undefined && obj[otherKey] !== null && obj[otherKey] !== "") {
      return obj[otherKey];
    }
  }

  return null;
};

/**
 * Find the best string field value from multiple candidates
 * Returns the first non-empty string value found
 */
export const findBestStringField = (obj: any, candidates: string[]): string => {
  const value = getFirstMatchingValueCI(obj, candidates);

  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (value !== null && value !== undefined) {
    const stringValue = String(value).trim();
    if (stringValue) {
      return stringValue;
    }
  }

  return "";
};

// ============================================================================
// MEDIA & THUMBNAIL UTILITIES
// ============================================================================

/**
 * Get thumbnail URL for Django items using temporaryUrlUtils
 * This handles images, videos, and generates CDN thumbnails as needed
 */
export const getDjangoThumbnailUrl = (item: any): string | undefined => {
  const thumbnailUrl = constructThumbnailUrl(item);
  if (!thumbnailUrl) return undefined;

  // Django thumbnail endpoints often don't include an extension (e.g. `/api/user/media/:id/thumbnail/`)
  // but still serve an image content-type. Treat them as valid thumbnails.
  if (thumbnailUrl.includes("/thumbnail/")) return thumbnailUrl;

  return isImageUrl(thumbnailUrl) ? thumbnailUrl : undefined;
};

/**
 * Extract string value from complex field structures
 * Handles nested objects, arrays, and various data formats
 */
export const extractStringValue = (value: any): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return String(value);

  if (Array.isArray(value)) {
    return value.map((v) => extractStringValue(v)).join(", ");
  }

  if (typeof value === "object" && value !== null) {
    // Handle common object patterns
    if (value.name) return extractStringValue(value.name);
    if (value.title) return extractStringValue(value.title);
    if (value.label) return extractStringValue(value.label);
    if (value.value) return extractStringValue(value.value);

    // Handle choice fields and other complex structures
    if (value.choice_values && Array.isArray(value.choice_values)) {
      return value.choice_values.join(", ");
    }

    if (value.other_values && Array.isArray(value.other_values)) {
      return value.other_values.join(", ");
    }

    // Last resort: stringify the object
    return JSON.stringify(value);
  }

  return String(value || "");
};

// ============================================================================
// FULCRUM FIELD PROCESSING FUNCTIONS
// ============================================================================

/**
 * Extract field value from Fulcrum form_values using field ID mapping
 */
export const extractFulcrumField = (formValues: Record<string, any>, fieldId: string, fieldType: string = "text"): any => {
  const value = formValues[fieldId];

  if (value === null || value === undefined) {
    return null;
  }

  // Handle complex field types
  switch (fieldType) {
    case "choice_field":
    case "classification_field":
      if (value && typeof value === "object") {
        // Combine choice_values and other_values
        const choices = value.choice_values || [];
        const others = value.other_values || [];
        const combined = [...choices, ...others];
        return combined.length > 0 ? combined.join(", ") : null;
      }
      return value;

    case "photo_field":
      if (Array.isArray(value)) {
        return value.map((photo) => ({
          photo_id: photo.photo_id,
          caption: photo.caption || null
        }));
      }
      return value;

    case "record_link_field":
      if (Array.isArray(value)) {
        return value.map((link) => link.record_id).filter(Boolean);
      }
      return value;

    default:
      // Handle complex objects even when field type is not explicitly known
      if (value && typeof value === "object") {
        // Handle choice fields that might not have correct type detection
        if (value.choice_values && Array.isArray(value.choice_values)) {
          const choices = value.choice_values || [];
          const others = value.other_values || [];
          const combined = [...choices, ...others];
          return combined.length > 0 ? combined.join(", ") : null;
        }

        // Handle other common object patterns
        if (value.other_values && Array.isArray(value.other_values)) {
          return value.other_values.join(", ");
        }

        if (value.value) {
          return value.value;
        }

        if (value.display) {
          return value.display;
        }

        if (value.name) {
          return value.name;
        }

        // If it's an array, join it
        if (Array.isArray(value)) {
          return value.map((v) => (typeof v === "object" ? v.name || v.value || v.display || JSON.stringify(v) : String(v))).join(", ");
        }

        // Last resort: stringify but avoid [object Object]
        try {
          const stringified = JSON.stringify(value);
          if (stringified !== "{}" && stringified !== "[]") {
            return stringified;
          }
        } catch (e) {
          // JSON.stringify failed
        }

        return String(value);
      }

      return value;
  }
};

/**
 * Build a flat index of fieldId -> { label, type } from a Fulcrum form schema.
 * Traverses nested structures (sections/repeatables) so ALL fields are discoverable.
 */
export const buildFieldIndexFromSchema = (formSchema: any): Record<string, { label: string; type: string }> => {
  const index: Record<string, { label: string; type: string }> = {};

  const walk = (elements: any[]) => {
    if (!Array.isArray(elements)) return;
    for (const el of elements) {
      const key: string | undefined = el?.key;
      const label: string | undefined = el?.label || el?.data_name || el?.key;
      const type: string = el?.type || "text";

      if (key) {
        index[key] = { label: label || key, type };
      }

      // Recurse into nested containers (sections, repeatables, groups)
      if (Array.isArray(el?.elements) && el.elements.length > 0) {
        walk(el.elements);
      }
    }
  };

  if (formSchema?.elements) {
    walk(formSchema.elements);
  }

  return index;
};

/**
 * Map form field IDs to human-readable names using form schema.
 * Uses a flattened field index so nested fields are also resolved.
 */
export const mapFormFields = (formValues: Record<string, any>, formSchema: any): Record<string, any> => {
  if (!formSchema) {
    return formValues;
  }

  const fieldIndex = buildFieldIndexFromSchema(formSchema);

  const mappedFields: Record<string, any> = {};

  // Iterate over the actual values we have and resolve labels/types from the index
  Object.entries(formValues).forEach(([fieldId, _value]) => {
    const meta = fieldIndex[fieldId];
    const readableLabel = meta?.label || fieldId;
    const fieldType = meta?.type || "text";
    mappedFields[readableLabel] = extractFulcrumField(formValues, fieldId, fieldType);
  });

  return mappedFields;
};

/**
 * Fetch and resolve linked Fulcrum records to get actual entity details
 * This transforms generic field relationships into meaningful entity relationships
 */
export const resolveLinkedRecords = async (
  recordLinkFields: [string, any[]][],
  apiEndpoints: ApiEndpoint[],
  formToRelationshipMapping: Map<string, string>,
  userProfile: any,
  formSchema?: any
): Promise<Record<string, RelatedEntityItem[]>> => {
  const resolvedRelationships: Record<string, RelatedEntityItem[]> = {};

  // Import the Fulcrum service dynamically to avoid circular dependencies
  const { fetchFulcrumRecordById, fetchFulcrumForm } = await import("../fulcrumLibService");

  // Process each record link field
  for (const [fieldId, links] of recordLinkFields) {
    if (!Array.isArray(links) || links.length === 0) continue;

    console.log(`[FulcrumAdapter] Resolving field ${fieldId} with ${links.length} links`);

    // Process each link in the field
    for (const [index, link] of links.entries()) {
      if (!link.record_id) continue;

      try {
        console.log(`[FulcrumAdapter] Fetching linked record: ${link.record_id}`);

        // Fetch the linked record
        const linkedRecord = await fetchFulcrumRecordById(link.record_id, userProfile);

        if (linkedRecord && linkedRecord.form_id) {
          // Determine the relationship key using form mapping
          const relationshipKey = formToRelationshipMapping.get(linkedRecord.form_id);

          if (relationshipKey) {
            console.log(`[FulcrumAdapter] Found form mapping: ${linkedRecord.form_id} -> ${relationshipKey}`);

            // Extract meaningful name from the linked record using dynamic field detection
            let linkedName = "Unknown Record";
            let linkedFormSchema: any = null;
            if (linkedRecord.form_values) {
              // Get the form schema for the linked record to identify name fields
              linkedFormSchema = await fetchFulcrumForm(linkedRecord.form_id, userProfile);

              // Use the DEFAULT_FULCRUM_MAPPING to find name fields dynamically
              for (const nameField of DEFAULT_FULCRUM_MAPPING.name) {
                // First try direct field matching
                if (linkedRecord.form_values[nameField]) {
                  linkedName = linkedRecord.form_values[nameField];
                  break;
                }

                // Then try to find fields in form schema that match name patterns
                if (linkedFormSchema?.form?.elements) {
                  const nameElement = findElementByType(linkedFormSchema.form.elements, ["TextField", "ChoiceField"], nameField);
                  if (nameElement && linkedRecord.form_values[nameElement.key]) {
                    const value = linkedRecord.form_values[nameElement.key];
                    linkedName = extractValueFromField(value);
                    break;
                  }
                }
              }

              // If still no name found, try to find any text field that might be a name
              if (linkedName === "Unknown Record" && linkedFormSchema?.form?.elements) {
                const firstTextField = findElementByType(linkedFormSchema.form.elements, ["TextField"], null);
                if (firstTextField && linkedRecord.form_values[firstTextField.key]) {
                  linkedName =
                    extractValueFromField(linkedRecord.form_values[firstTextField.key]) || `Record ${linkedRecord.id.substring(0, 8)}`;
                } else {
                  linkedName = `Record ${linkedRecord.id.substring(0, 8)}`;
                }
              }
            }

            // Create the resolved relationship item using unified helpers
            const linkedEndpoint = apiEndpoints.find((ep) => ep.formId === linkedRecord.form_id);
            const transformedLinkedFields = mapFormFields(linkedRecord.form_values || {}, linkedFormSchema?.form || linkedFormSchema);

            // Use unified helpers for proper name/description extraction
            const { extractUnifiedDisplayName, extractUnifiedDisplayDescription } = await import("../dataSourceAdapters");

            const resolvedItem: RelatedEntityItem = {
              id: linkedRecord.id, // Keep as UUID string
              entity_name: extractUnifiedDisplayName({
                endpoint: linkedEndpoint,
                formValues: transformedLinkedFields,
                rawItem: linkedRecord,
                fieldMapping: DEFAULT_FULCRUM_MAPPING,
                userProfile,
                formSchema: linkedFormSchema
              }),
              entity_description: extractUnifiedDisplayDescription({
                endpoint: linkedEndpoint,
                formValues: transformedLinkedFields,
                rawItem: linkedRecord,
                fieldMapping: DEFAULT_FULCRUM_MAPPING,
                userProfile,
                formSchema: linkedFormSchema
              }),
              rel_description: `Linked ${relationshipKey}`,
              is_primary: index === 0,
              entity_thumbnail: null // Could be enhanced with photo extraction
            };

            // Group by relationship key (e.g., "location", "native_animal")
            if (!resolvedRelationships[relationshipKey]) {
              resolvedRelationships[relationshipKey] = [];
            }
            resolvedRelationships[relationshipKey].push(resolvedItem);

            console.log(`[FulcrumAdapter] Successfully resolved ${link.record_id} as ${relationshipKey}: ${linkedName}`);
          } else {
            console.warn(`[FulcrumAdapter] No relationship mapping found for form ${linkedRecord.form_id}`);

            // Fallback: use generic field-based key
            const fieldLabel = getFieldLabel(fieldId, formSchema);
            const fallbackKey =
              fieldLabel
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "") || `field_${fieldId}`;

            const fallbackItem: RelatedEntityItem = {
              id: linkedRecord.id,
              entity_name: `Record ${linkedRecord.id.substring(0, 8)}`,
              entity_description: `Linked record (unknown form type)`,
              rel_description: fieldLabel,
              is_primary: index === 0,
              entity_thumbnail: null
            };

            if (!resolvedRelationships[fallbackKey]) {
              resolvedRelationships[fallbackKey] = [];
            }
            resolvedRelationships[fallbackKey].push(fallbackItem);
          }
        } else {
          console.warn(`[FulcrumAdapter] Failed to fetch linked record: ${link.record_id}`);
        }
      } catch (error) {
        console.error(`[FulcrumAdapter] Error fetching linked record ${link.record_id}:`, error);

        // Create a placeholder for failed fetches
        const fieldLabel = getFieldLabel(fieldId, formSchema);
        const fallbackKey =
          fieldLabel
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "") || `field_${fieldId}`;

        const errorItem: RelatedEntityItem = {
          id: link.record_id,
          entity_name: `Record ${link.record_id.substring(0, 8)} (unavailable)`,
          entity_description: "This record could not be loaded",
          rel_description: fieldLabel,
          is_primary: index === 0,
          entity_thumbnail: null
        };

        if (!resolvedRelationships[fallbackKey]) {
          resolvedRelationships[fallbackKey] = [];
        }
        resolvedRelationships[fallbackKey].push(errorItem);
      }
    }
  }

  console.log(`[FulcrumAdapter] Relationship resolution complete. Resolved keys:`, Object.keys(resolvedRelationships));

  return resolvedRelationships;
};

// ============================================================================
// MOVED FROM dataSourceAdapters.ts TO BREAK CIRCULAR DEPENDENCIES
// ============================================================================

/**
 * Detect Fulcrum region based on form ID length
 */
export const detectFulcrumRegion = (formId?: string): "au" | "us" => {
  if (!formId) return "au";
  return formId.length > 30 ? "au" : "us";
};

/**
 * Construct Fulcrum photo URL with proper region detection
 */
export const constructFulcrumPhotoUrl = (photoId: string, formId?: string, thumbnail: boolean = true): string => {
  if (!photoId) {
    throw new Error(`Invalid photoId: ${photoId}`);
  }

  const region = detectFulcrumRegion(formId);
  const baseUrl = region === "au" ? "https://api.fulcrumapp-au.com" : "https://api.fulcrumapp.com";
  const endpoint = thumbnail ? `/api/v2/photos/${photoId}/thumbnail.jpg` : `/api/v2/photos/${photoId}.jpg`;
  return `${baseUrl}${endpoint}`;
};

// constructFulcrumWebUrl has moved to FulcrumAdapter.ts

/**
 * Convert GeoJSON features to CultureMap entity items.
 *
 * @param geoJsonData - Raw GeoJSON FeatureCollection from Fulcrum.
 * @param endpoint    - The API endpoint configuration for this layer.
 * @param formSchema  - Optional Fulcrum form schema. When provided, feature
 *   properties are normalised from raw field IDs to human-readable labels
 *   (via `mapFormFields`) before name/description extraction. This ensures
 *   parity with `processFullRecord`, which always performs this translation
 *   for individually-fetched records (popup, related items).
 */
export const geoJsonToCultureMapAdapter = (geoJsonData: any, endpoint?: ApiEndpoint, formSchema?: any): FlexibleDataItem[] => {
  if (!geoJsonData?.features || !Array.isArray(geoJsonData.features)) {
    console.warn("[GeoJSON-Adapter] Invalid GeoJSON data - no features array");
    return [];
  }

  return geoJsonData.features.map((feature: any, index: number): FlexibleDataItem => {
    const properties = feature.properties || {};
    const geometry = feature.geometry;

    /**
     * Normalise property keys using the form schema when available.
     *
     * Fulcrum's GeoJSON endpoint flattens form_values into the top-level
     * properties object. The keys it uses may differ from the human-readable
     * labels defined in the form schema (e.g. the schema might have "Site Name"
     * while the GeoJSON key is "site_name" or the raw field UUID "ea1a").
     *
     * `mapFormFields` translates whichever keys match a schema field's UUID /
     * data_name back to the schema label. Keys that don't match a schema field
     * are passed through unchanged, so no data is lost.
     *
     * This mirrors what `processFullRecord` does before calling the same
     * extraction helpers, making name/description resolution consistent across
     * all surfaces (sidebar, map markers, popup, related-item cards).
     */
    const normalizedProps = formSchema ? mapFormFields(properties, formSchema) : properties;

    const name = extractUnifiedDisplayName({ endpoint, formValues: normalizedProps, rawItem: properties });
    const description = extractUnifiedDisplayDescription({ endpoint, formValues: normalizedProps, rawItem: properties });

    // Photo detection and URL construction
    let thumbnail = undefined;
    let primaryImage = undefined;

    const photoFields = Object.entries(properties).filter(
      ([key, value]) =>
        (key.toLowerCase().includes("photo") &&
          typeof value === "string" &&
          value.length > 0 &&
          value !== "," &&
          !key.toLowerCase().includes("caption") &&
          !key.toLowerCase().includes("url")) ||
        (Array.isArray(value) && value.length > 0 && value.some((item: any) => item && typeof item === "object" && item.photo_id))
    );

    if (photoFields.length > 0) {
      const firstPhotoField = photoFields[0];
      const photoData = firstPhotoField[1];
      let photoId = null;

      if (Array.isArray(photoData) && photoData.length > 0) {
        const firstPhoto = photoData[0];
        if (firstPhoto?.photo_id) {
          photoId = firstPhoto.photo_id;
        }
      } else if (typeof photoData === "string" && photoData.length > 0) {
        if (!photoData.includes("http") && !photoData.includes("thumbnail.jpg") && !photoData.includes("/photos/")) {
          const photoIds = photoData
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
          if (photoIds.length > 0) {
            const candidateId = photoIds[0];
            const invalidValues = ["yes", "no", "true", "false", "null", "undefined", "none", "n/a"];
            const isValidPhotoId =
              !candidateId.includes("/") &&
              !candidateId.includes(".") &&
              candidateId.length >= 8 &&
              !invalidValues.includes(candidateId.toLowerCase()) &&
              /^[a-f0-9-]{8,}$/i.test(candidateId);

            if (isValidPhotoId) {
              photoId = candidateId;
            }
          }
        }
      }

      if (photoId) {
        const formId = properties.form_id || endpoint?.formId;
        thumbnail = constructFulcrumPhotoUrl(photoId, formId, true);
        const photoUrl = constructFulcrumPhotoUrl(photoId, formId, false);
        primaryImage = {
          url: photoUrl,
          thumbnail: thumbnail
        };
      }
    }

    console.log(`[GeoJSON-Adapter] Processing feature ${index + 1}:`, {
      itemId: properties._record_id || properties.id,
      allPropertyKeys: Object.keys(properties),
      sampleProperties: Object.fromEntries(
        Object.entries(properties).slice(0, 10) // Show first 10 properties for debugging
      )
    });

    return {
      // Spread properties first so form labels like "name"/"description" don't overwrite computed strings
      ...properties,

      id: properties._record_id || properties.record_id || properties.fulcrum_id || properties.id || feature.id || `feature-${index}`,
      name,
      description,
      thumbnail,
      primary_image: primaryImage,
      geometry,

      properties: {
        ...properties,
        extractionMethod: "geoJsonToCultureMapAdapter",
        fromGeoJSON: true,
        dataSourceType: "fulcrum",
        form_id: properties.form_id || endpoint?.formId,
        coordinates: geometry?.type === "Point" ? (geometry.coordinates as [number, number]) : undefined
      },
      entityType: endpoint?.tableName || "fulcrum-record",
      layerId: endpoint?.tableName || "fulcrum-layer"
    } as FlexibleDataItem;
  });
};

// Field extraction options interface
export interface FieldExtractionOptions {
  endpoint?: ApiEndpoint;
  formValues?: Record<string, any>;
  rawItem?: any;
  fieldMapping?: FieldMapping;
  userProfile?: any;
  formSchema?: any;
}

/**
 * Extract display name using unified priority system
 * Prioritizes endpoint-specific field mappings over generic patterns
 */
export const extractUnifiedDisplayName = (options: FieldExtractionOptions): string => {
  const { endpoint, formValues = {}, rawItem = {}, fieldMapping } = options;

  // 1. PRIMARY: Endpoint-specific field mapping (from config.json)
  if (endpoint?.dataSourceConfig?.fieldMapping?.name) {
    const configuredFields = endpoint.dataSourceConfig.fieldMapping.name as string[];
    const mappingArray = Array.isArray(configuredFields) ? configuredFields : [configuredFields];

    for (const fieldName of mappingArray) {
      const value = getFirstMatchingValueCI(formValues, [fieldName]);
      if (value && String(value).trim() !== "" && value !== "NULL") {
        return String(value);
      }
    }
  }
  // 2. Generic field mapping fallback (from adapter config)
  if (fieldMapping?.name) {
    const mappedName = extractField({ ...rawItem, ...formValues }, fieldMapping.name);
    if (mappedName && mappedName !== "Unnamed Item" && mappedName !== "NULL") {
      const s = typeof mappedName === "string" ? mappedName : extractStringValue(mappedName);
      if (s.trim()) {
        return s;
      }
    }
  }

  // 3. Schema-mapped transformed fields (common patterns)
  const fromTransformed = getFirstMatchingValueCI(formValues, COMMON_NAME_CANDIDATES);
  if (fromTransformed && fromTransformed !== "NULL") {
    const s = typeof fromTransformed === "string" ? fromTransformed : extractStringValue(fromTransformed);
    if (s.trim()) {
      return s;
    }
  }

  // 4. Intelligent field selection (best available string field)
  const best = findBestStringField(formValues, COMMON_NAME_CANDIDATES);
  if (best && best !== "NULL") {
    return best;
  }

  // 5. Raw item fallbacks (guard against non-string values like nested objects)
  for (const key of ["name", "label", "title"] as const) {
    const v = rawItem[key];
    if (v && typeof v === "string" && v !== "NULL") return v;
  }

  // 6. DEPRECATED: Legacy Fulcrum ID patterns (only as last resort)
  for (const fieldId of COMMON_FULCRUM_NAME_IDS) {
    const value = extractFulcrumField(formValues, fieldId, fieldId === "1c63" ? "choice_field" : "text");
    if (value && value !== "NULL") {
      const s = typeof value === "string" ? value : extractStringValue(value);
      if (s.trim()) {
        return s;
      }
    }
  }

  // 7. Final fallback
  const fallbackName = `Record ${rawItem.id?.substring(0, 8) || "unknown"}`;
  return fallbackName;
};

/**
 * Extract display description using unified priority system
 * Prioritizes endpoint-specific field mappings and contextual generation
 */
export const extractUnifiedDisplayDescription = (options: FieldExtractionOptions): string => {
  const { endpoint, formValues = {}, rawItem = {}, fieldMapping } = options;

  // 1. PRIMARY: Endpoint-specific field mapping (from config.json)
  if (endpoint?.dataSourceConfig?.fieldMapping?.description) {
    const configuredFields = endpoint.dataSourceConfig.fieldMapping.description as string[];
    const mappingArray = Array.isArray(configuredFields) ? configuredFields : [configuredFields];

    for (const fieldName of mappingArray) {
      const value = getFirstMatchingValueCI(formValues, [fieldName]);
      const s = value != null ? (typeof value === "string" ? value : extractStringValue(value)) : "";
      if (s.trim() && s !== "NULL") {
        return s;
      }
    }
  }

  // 2. Generic field mapping fallback (from adapter config)
  if (fieldMapping?.description) {
    const mappedDescription = extractField({ ...rawItem, ...formValues }, fieldMapping.description);
    if (mappedDescription && mappedDescription !== "NULL") {
      const desc = extractStringValue(mappedDescription);
      if (desc && desc.trim() !== "") {
        return desc;
      }
    }
  }

  // 3. Schema-mapped transformed fields (common patterns)
  const fromTransformedRaw = getFirstMatchingValueCI(formValues, COMMON_DESCRIPTION_CANDIDATES);
  if (fromTransformedRaw && fromTransformedRaw !== "NULL") {
    const s = typeof fromTransformedRaw === "string" ? fromTransformedRaw : extractStringValue(fromTransformedRaw);
    if (s.trim()) {
      return s;
    }
  }

  // 4. Raw item fallbacks
  const rawDescs = [extractStringValue(rawItem.description), extractStringValue(rawItem.notes)].filter(
    (desc) => desc && desc.trim() !== "" && desc !== "NULL"
  );

  if (rawDescs.length > 0) {
    return rawDescs[0] as string;
  }

  // 5. DEPRECATED: Legacy Fulcrum ID patterns (only as last resort)
  for (const fieldId of COMMON_FULCRUM_DESC_IDS) {
    const value = extractStringValue(extractFulcrumField(formValues, fieldId));
    if (value && value.trim() !== "" && value !== "NULL") {
      return value;
    }
  }

  // 6. ENHANCED: Generate contextual description from available data
  const contextual = generateContextualDescription(formValues, rawItem);
  if (contextual) {
    return contextual;
  }
  return "";
};

/**
 * Generate contextual description from available form data when no explicit description exists
 */
const generateContextualDescription = (formValues: Record<string, any>, rawItem: any): string => {
  const contextParts: string[] = [];

  // Start with status information if available (most important context)
  if (rawItem?.status && typeof rawItem.status === "string" && rawItem.status !== "NULL") {
    contextParts.push(rawItem.status);
  }

  // Try to find date/time information for context
  const dateFields = ["Date recorded", "date_recorded", "Date", "date", "created_at", "updated_at"];
  const timeFields = ["Time", "time", "Time recorded", "time_recorded"];

  let dateInfo = "";
  let timeInfo = "";

  // Look for date information
  for (const field of dateFields) {
    const raw = getFirstMatchingValueCI(formValues, [field]);
    if (raw && typeof raw === "string" && raw !== "NULL") {
      try {
        let dateStr = raw;
        if (raw.includes("-")) {
          const parts = raw.split("-");
          if (parts.length === 3) {
            dateStr = `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }
        dateInfo = dateStr;
        break;
      } catch (e) {
        dateInfo = raw;
      }
    }
  }

  // Look for time information
  for (const field of timeFields) {
    const raw = getFirstMatchingValueCI(formValues, [field]);
    if (raw && typeof raw === "string" && raw !== "NULL") {
      timeInfo = raw;
      break;
    }
  }

  // Build temporal context
  if (dateInfo || timeInfo) {
    let timePart = "";
    if (dateInfo && timeInfo) {
      timePart = `Recorded on ${dateInfo} at ${timeInfo}`;
    } else if (dateInfo) {
      timePart = `Recorded on ${dateInfo}`;
    } else if (timeInfo) {
      timePart = `Recorded at ${timeInfo}`;
    }

    if (timePart) {
      contextParts.push(timePart);
    }
  }

  // Look for additional status or type information from form fields
  const statusFields = ["status", "type", "category", "classification", "activity_type"];
  for (const field of statusFields) {
    const raw = getFirstMatchingValueCI(formValues, [field]);
    const str = typeof raw === "string" ? raw : raw != null ? extractStringValue(raw) : "";
    if (str && str !== "NULL" && !contextParts.some((part) => part.toLowerCase().includes(str.toLowerCase()))) {
      contextParts.push(`Type: ${str}`);
      break;
    }
  }

  // Look for location context if not already in the name (and different from name)
  const locationFields = ["location", "site", "place", "area", "region"];
  for (const field of locationFields) {
    const raw = getFirstMatchingValueCI(formValues, [field]);
    const str = typeof raw === "string" ? raw : raw != null ? extractStringValue(raw) : "";
    if (str && str !== "NULL" && !contextParts.some((part) => part.includes(str))) {
      contextParts.push(`Location: ${str}`);
      break;
    }
  }

  // If we have coordinates, add location context
  if (rawItem?.latitude && rawItem?.longitude && contextParts.length === 0) {
    contextParts.push(`Located at ${rawItem.latitude.toFixed(6)}, ${rawItem.longitude.toFixed(6)}`);
  }

  // Combine context parts with bullet separator
  if (contextParts.length > 0) {
    return contextParts.join(" • ");
  }

  // Final fallback - try to use form name or type
  if (rawItem?.form_name) {
    return `${rawItem.form_name} record`;
  }

  return "";
};

/**
 * Organize properties into user-relevant and internal categories
 * This implements the standardized property organization from the improvement plan
 */
export const organizeProperties = (
  formValues: Record<string, any>,
  rawItem: any,
  options: { includeInternal?: boolean } = {}
): { userRelevant: Record<string, any>; internal: Record<string, any> } => {
  const userRelevant: Record<string, any> = {};
  const internal: Record<string, any> = {};

  // Define field categories
  const INTERNAL_FIELD_PATTERNS = [
    // Technical identifiers
    /^(source|id|uuid|guid|_id)$/i,
    /source.*id/i,
    /.*_id$/i,
    /^(created|updated)_by.*id$/i,

    // Fulcrum-specific technical fields
    /^(form_id|project_id|changeset_id|record_series_id)$/i,
    /^assigned_to.*id$/i,

    // Raw/debug data
    /^(raw_|debug_|_debug)/i,

    // Processing metadata
    /processing.*metadata/i,
    /adapter.*version/i,

    // System fields
    /^system_/i,
    /^_/i, // Fields starting with underscore

    // Accuracy/technical location data
    /(accuracy|speed|course|altitude)/i,

    // Version/status tracking
    /^version$/i,
    /^client_(created|updated)_at$/i
  ];

  const USER_PRIORITY_PATTERNS = [
    // Core user data
    /^(name|title|label|description|notes|comment)/i,
    /location.*name/i,
    /site.*name/i,

    // Dates and times (user-friendly)
    /^(date|time)(?!.*id)/i,
    /recorded/i,

    // Status and type information
    /^(status|type|category|classification)/i,

    // Measurements and observations
    /^(measurement|observation|reading|value)/i,
    /^(temperature|pressure|depth|height|width|length)/i,

    // Species and biological data
    /^(species|animal|plant|flora|fauna)/i,
    /^(common.*name|scientific.*name)/i,

    // Activity and job information
    /^(activity|job|task|work)/i,

    // Location context (not technical)
    /^(area|region|zone|sector)/i
  ];

  // Categorize form values
  for (const [key, value] of Object.entries(formValues)) {
    // Skip null/undefined values
    if (value === null || value === undefined || value === "NULL") {
      continue;
    }

    const isInternal = INTERNAL_FIELD_PATTERNS.some((pattern) => pattern.test(key));
    const isUserPriority = USER_PRIORITY_PATTERNS.some((pattern) => pattern.test(key));

    if (isInternal && !isUserPriority) {
      internal[key] = value;
    } else {
      userRelevant[key] = value;
    }
  }

  // Handle raw item properties
  if (rawItem) {
    // User-relevant raw item fields
    const userRawFields = ["status", "created_at", "updated_at", "latitude", "longitude"];
    const internalRawFields = [
      "form_id",
      "project_id",
      "changeset_id",
      "record_series_id",
      "assigned_to_id",
      "created_by_id",
      "updated_by_id",
      "client_created_at",
      "client_updated_at",
      "version",
      "horizontal_accuracy",
      "vertical_accuracy",
      "speed",
      "course",
      "altitude"
    ];

    for (const field of userRawFields) {
      if (rawItem[field] !== null && rawItem[field] !== undefined) {
        userRelevant[field] = rawItem[field];
      }
    }

    if (options.includeInternal) {
      for (const field of internalRawFields) {
        if (rawItem[field] !== null && rawItem[field] !== undefined) {
          internal[field] = rawItem[field];
        }
      }
    }
  }

  return { userRelevant, internal };
};

// ============================================================================
// CACHE-AWARE ADAPTER FUNCTIONS
// ============================================================================

/**
 * Fetch form schema with caching support
 * Uses the advanced caching system to reduce API calls and improve performance
 */
export const fetchFormSchemaWithCache = async (formId: string, userProfile: any): Promise<any> => {
  // Dynamic import to avoid circular dependencies
  const { getCachedFormSchema, cacheFormSchema } = await import("../relatedItemsCache");

  // Check cache first
  const cached = getCachedFormSchema(formId);
  if (cached) {
    console.log(`[Cache] Form schema cache hit for ${formId}`);
    return cached;
  }

  console.log(`[Cache] Form schema cache miss for ${formId}, fetching...`);

  try {
    // Import and fetch from Fulcrum service
    const { fetchFulcrumForm } = await import("../fulcrumLibService");
    const formResponse = await fetchFulcrumForm(formId, userProfile);
    const schema = formResponse?.form || formResponse;

    if (schema) {
      // Cache the result for future use (7 days TTL for form schemas)
      await cacheFormSchema(formId, schema);
      console.log(`[Cache] Cached form schema for ${formId}`);
    }

    return schema;
  } catch (error) {
    console.warn(`[Cache] Failed to fetch form schema for ${formId}:`, error);
    return null;
  }
};

/**
 * Resolve linked records with caching support
 * Enhanced version of resolveLinkedRecords that uses the cache system
 */
export const resolveLinkedRecordsWithCache = async (
  recordLinkFields: [string, any[]][],
  apiEndpoints: ApiEndpoint[],
  formToRelationshipMapping: Map<string, string>,
  userProfile: any,
  formSchema?: any
): Promise<Record<string, RelatedEntityItem[]>> => {
  const resolvedRelationships: Record<string, RelatedEntityItem[]> = {};

  // Dynamic import to avoid circular dependencies
  const { getCachedRelatedItems, cacheRelatedItems } = await import("../relatedItemsCache");

  // Import Fulcrum service functions
  const { fetchFulcrumRecordById } = await import("../fulcrumLibService");

  // Process each record link field
  for (const [fieldId, links] of recordLinkFields) {
    if (!Array.isArray(links) || links.length === 0) continue;

    console.log(`[Cache] Resolving field ${fieldId} with ${links.length} links`);

    // Process each link in the field
    for (const [index, link] of links.entries()) {
      if (!link.record_id) continue;

      // Check cache for this specific record
      const cacheKey = `record_${link.record_id}`;
      const { getCachedData, cacheData } = await import("../relatedItemsCache");
      const cachedRecord = getCachedData<any>(cacheKey, "fulcrum");

      let linkedRecord = cachedRecord;

      if (!linkedRecord) {
        try {
          console.log(`[Cache] Record cache miss for ${link.record_id}, fetching...`);
          linkedRecord = await fetchFulcrumRecordById(link.record_id, userProfile);

          if (linkedRecord) {
            // Cache the record for future use (1 hour TTL for records)
            await cacheData(cacheKey, "fulcrum", linkedRecord);
            console.log(`[Cache] Cached record ${link.record_id}`);
          }
        } catch (error) {
          console.error(`[Cache] Error fetching linked record ${link.record_id}:`, error);
          continue;
        }
      } else {
        console.log(`[Cache] Record cache hit for ${link.record_id}`);
      }

      if (linkedRecord && linkedRecord.form_id) {
        // Determine the relationship key using form mapping
        const relationshipKey = formToRelationshipMapping.get(linkedRecord.form_id);

        if (relationshipKey) {
          console.log(`[Cache] Found form mapping: ${linkedRecord.form_id} -> ${relationshipKey}`);

          // Extract meaningful name from the linked record using cached form schema
          let linkedName = "Unknown Record";
          let linkedFormSchema: any = null;

          if (linkedRecord.form_values) {
            // Get the form schema using cache-aware function
            linkedFormSchema = await fetchFormSchemaWithCache(linkedRecord.form_id, userProfile);

            // Use the DEFAULT_FULCRUM_MAPPING to find name fields dynamically
            for (const nameField of DEFAULT_FULCRUM_MAPPING.name) {
              // First try direct field matching
              if (linkedRecord.form_values[nameField]) {
                linkedName = linkedRecord.form_values[nameField];
                break;
              }

              // Then try to find fields in form schema that match name patterns
              if (linkedFormSchema?.elements) {
                const nameElement = findElementByType(linkedFormSchema.elements, ["TextField", "ChoiceField"], nameField);
                if (nameElement && linkedRecord.form_values[nameElement.key]) {
                  const value = linkedRecord.form_values[nameElement.key];
                  linkedName = extractValueFromField(value);
                  break;
                }
              }
            }

            // If still no name found, try to find any text field that might be a name
            if (linkedName === "Unknown Record" && linkedFormSchema?.elements) {
              const firstTextField = findElementByType(linkedFormSchema.elements, ["TextField"], null);
              if (firstTextField && linkedRecord.form_values[firstTextField.key]) {
                linkedName =
                  extractValueFromField(linkedRecord.form_values[firstTextField.key]) || `Record ${linkedRecord.id.substring(0, 8)}`;
              } else {
                linkedName = `Record ${linkedRecord.id.substring(0, 8)}`;
              }
            }
          }

          // Create the resolved relationship item using unified helpers
          const linkedEndpoint = apiEndpoints.find((ep) => ep.formId === linkedRecord.form_id);
          const transformedLinkedFields = mapFormFields(linkedRecord.form_values || {}, linkedFormSchema);

          // Use unified helpers for proper name/description extraction
          const { extractUnifiedDisplayName, extractUnifiedDisplayDescription } = await import("../dataSourceAdapters");

          // Extract thumbnail using the same pattern as individual records:
          // scan form_values for any photo array field and use the first photo_id
          let linkedThumbnail: string | null = null;
          if (linkedRecord.form_values) {
            const photoEntry = Object.values(linkedRecord.form_values).find(
              (v): v is any[] => Array.isArray(v) && v.length > 0 && v[0]?.photo_id
            );
            if (photoEntry) {
              linkedThumbnail = constructFulcrumPhotoUrl(photoEntry[0].photo_id, linkedRecord.form_id, true);
            }
          }

          const resolvedItem: RelatedEntityItem = {
            id: linkedRecord.id, // Keep as UUID string
            entity_name: extractUnifiedDisplayName({
              endpoint: linkedEndpoint,
              formValues: transformedLinkedFields,
              rawItem: linkedRecord,
              fieldMapping: DEFAULT_FULCRUM_MAPPING,
              userProfile,
              formSchema: linkedFormSchema
            }),
            entity_description: extractUnifiedDisplayDescription({
              endpoint: linkedEndpoint,
              formValues: transformedLinkedFields,
              rawItem: linkedRecord,
              fieldMapping: DEFAULT_FULCRUM_MAPPING,
              userProfile,
              formSchema: linkedFormSchema
            }),
            rel_description: `Linked ${relationshipKey}`,
            is_primary: index === 0,
            entity_thumbnail: linkedThumbnail
          };

          // Group by relationship key (e.g., "location", "native_animal")
          if (!resolvedRelationships[relationshipKey]) {
            resolvedRelationships[relationshipKey] = [];
          }
          resolvedRelationships[relationshipKey].push(resolvedItem);

          console.log(`[Cache] Successfully resolved ${link.record_id} as ${relationshipKey}: ${linkedName}`);
        } else {
          console.warn(`[Cache] No relationship mapping found for form ${linkedRecord.form_id}`);

          // Fallback: use generic field-based key
          const fieldLabel = getFieldLabel(fieldId, formSchema);
          const fallbackKey =
            fieldLabel
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "_")
              .replace(/^_+|_+$/g, "") || `field_${fieldId}`;

          const fallbackItem: RelatedEntityItem = {
            id: linkedRecord.id,
            entity_name: `Record ${linkedRecord.id.substring(0, 8)}`,
            entity_description: `Linked record (unknown form type)`,
            rel_description: fieldLabel,
            is_primary: index === 0,
            entity_thumbnail: null
          };

          if (!resolvedRelationships[fallbackKey]) {
            resolvedRelationships[fallbackKey] = [];
          }
          resolvedRelationships[fallbackKey].push(fallbackItem);
        }
      }
    }
  }

  console.log(`[Cache] Relationship resolution complete. Resolved keys:`, Object.keys(resolvedRelationships));

  return resolvedRelationships;
};

/**
 * Performance monitoring wrapper for adapter operations
 * Tracks operation timing and cache effectiveness
 */
export const withPerformanceMonitoring = async <T>(operationName: string, operation: () => Promise<T>): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await operation();
    const duration = performance.now() - startTime;

    console.log(`[Performance] ${operationName} completed in ${duration.toFixed(2)}ms`);

    // Track performance in cache system
    try {
      const { default: globalCache } = await import("../relatedItemsCache");
      globalCache.trackPerformance(operationName, duration);
    } catch (cacheError) {
      console.warn("[Performance] Failed to track performance metrics:", cacheError);
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    console.error(`[Performance] ${operationName} failed after ${duration.toFixed(2)}ms:`, error);

    // Track failed operations too
    try {
      const { default: globalCache } = await import("../relatedItemsCache");
      globalCache.trackPerformance(`${operationName}_FAILED`, duration);
    } catch (cacheError) {
      console.warn("[Performance] Failed to track failed operation metrics:", cacheError);
    }

    throw error;
  }
};
